import { Component, lazy, Suspense, useContext } from 'react'
import { BrowserRouter, Route, Routes, useNavigate, useLocation, useParams } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import { ThemeContextProvider, ThemeContext, GlobalStyles, LanguageContextProvider, LanguageContext } from './theme'
import WithNavigate from './Utils/WithNavigate'
import { Container, ProtectedRoute } from './Components'
import NavbarObj from './Library/Components/NavbarObj.jsx'
// import { ActivityTimeout } from './Services' // Temporaneamente disabilitato
import { SwallerProvider } from './Components/Swaller/SwallerContext'
import Loader from './Library/Tools/Loader'

// Lazy load delle pagine
const Landing = lazy(() => import('./Pages/Landing/Landing'))
const Login = lazy(() => import('./Pages/Login/Login'))
const FirstLogin = lazy(() => import('./Pages/Login/FirstLogin'))
const FirstOtp = lazy(() => import('./Pages/Login/FirstOtp'))
const ResetPassword = lazy(() => import('./Pages/Login/ResetPassword'))
const Home = lazy(() => import('./Pages/Home/Home'))
const Aziende = lazy(() => import('./Pages/Aziende/Aziende'))
const Preventivi = lazy(() => import('./Pages/Preventivi/Preventivi'))
const NuovoPreventivo = lazy(() => import('./Pages/Preventivi/NuovoPreventivo'))
const Fatture = lazy(() => import('./Pages/Fatture/Fatture'))
const NuovaFattura = lazy(() => import('./Pages/Fatture/NuovaFattura'))
const ModificaFattura = lazy(() => import('./Pages/Fatture/ModificaFattura'))
const Settaggi = lazy(() => import('./Pages/Settaggi/Settaggi'))
const Utenti = lazy(() => import('./Pages/Utenti/Utenti'))
const UtentePage = lazy(() => import('./Pages/Utenti/UtentePage'))
const NuovaAzienda = lazy(() => import('./Pages/Aziende/NuovaAzienda'))
const ClientePage = lazy(() => import('./Pages/Clienti/ClientePage'))
const Clienti = lazy(() => import('./Pages/Clienti/Clienti'))
const MagazzinoUnificato = lazy(() => import('./Pages/Magazzino/MagazzinoUnificato'))
const Ordini = lazy(() => import('./Pages/Ordini/Ordini'))
const Carrello = lazy(() => import('./Pages/Carrello/Carrello'))
const ModificaAzienda = lazy(() => import('./Pages/Aziende/ModificaAzienda'))
const ModificaPreventivo = lazy(() => import('./Pages/Preventivi/ModificaPreventivo'))
const Variabili = lazy(() => import('./Pages/Variabili/Variabili'))
const Servizi = lazy(() => import('./Pages/Servizi/Servizi'))
const ServizioPage = lazy(() => import('./Pages/Servizi/ServizioPage'))
const Fornitori = lazy(() => import('./Pages/Fornitori/Fornitori'))
const FornitorePage = lazy(() => import('./Pages/Fornitori/FornitorePage'))
const Investitori = lazy(() => import('./Pages/Investitori/Investitori'))
const InvestitorePage = lazy(() => import('./Pages/Investitori/InvestitorePage'))
const Investimenti = lazy(() => import('./Pages/Investimenti/Investimenti'))
const InvestimentoPage = lazy(() => import('./Pages/Investimenti/InvestimentoPage'))
const Sconti = lazy(() => import('./Pages/Sconti/Sconti'))
const NuovoSconto = lazy(() => import('./Pages/Sconti/NuovoSconto'))
const ModificaSconto = lazy(() => import('./Pages/Sconti/ModificaSconto'))
const Articoli = lazy(() => import('./Pages/Articoli/Articoli'))
const ArticoloPage = lazy(() => import('./Pages/Articoli/ArticoloPage'))
const Ricavi = lazy(() => import('./Pages/Ricavi/Ricavi'))
const RicavoPage = lazy(() => import('./Pages/Ricavi/RicavoPage'))
const Costi = lazy(() => import('./Pages/Costi/Costi'))
const CostoPage = lazy(() => import('./Pages/Costi/CostoPage'))
const Rimborsi = lazy(() => import('./Pages/Rimborsi/Rimborsi'))
const RimborsoPage = lazy(() => import('./Pages/Rimborsi/RimborsoPage'))
const Scatole = lazy(() => import('./Pages/Scatole/Scatole'))
const ModificaScatola = lazy(() => import('./Pages/Scatole/ModificaScatola'))
const Progetti = lazy(() => import('./Pages/Progetti/Progetti'))
const ProgettoPage = lazy(() => import('./Pages/Progetti/ProgettoPage'))
const Canoni = lazy(() => import('./Pages/Canoni/Canoni'))
const AggiungiCanoni = lazy(() => import('./Pages/Canoni/AggiungiCanoni'))
const ModificaCanoni = lazy(() => import('./Pages/Canoni/ModificaCanoni'))
const Analisi = lazy(() => import('./Pages/Analisi/AnalisiNew'))
const ProdottiEcommerce = lazy(() => import('./Pages/Ecommerce/ProdottiEcommerce'))
const ProdottoEcommercePage = lazy(() => import('./Pages/Ecommerce/ProdottoEcommercePage'))
const CategorieEcommerce = lazy(() => import('./Pages/Ecommerce/CategorieEcommerce'))
const ClientiEcommerce = lazy(() => import('./Pages/Ecommerce/ClientiEcommerce'))
const Costruzioni = lazy(() => import('./Pages/Costruzioni/Costruzioni'))
const CostruzionePage = lazy(() => import('./Pages/Costruzioni/CostruzionePage'))
const FotovoltaicoPage = lazy(() => import('./Pages/Costruzioni/FotovoltaicoPage'))
const Installazioni = lazy(() => import('./Pages/Installazioni/Installazioni'))
const InstallazionePage = lazy(() => import('./Pages/Installazioni/InstallazionePage'))
const BoardList = lazy(() => import('./Pages/Board/Board'))
const BoardPage = lazy(() => import('./Pages/Board/BoardPage'))
const TicketPage = lazy(() => import('./Pages/Ticket/TicketPage'))
const TicketDetailPage = lazy(() => import('./Pages/Ticket/TicketDetailPage'))
const Resoconti = lazy(() => import('./Pages/Resoconti/Resoconti'))
const ResocontoPage = lazy(() => import('./Pages/Resoconti/ResocontoPage'))
const Cronoprogrammi = lazy(() => import('./Pages/Cronoprogrammi/Cronoprogrammi'))
const CronoprogrammaPage = lazy(() => import('./Pages/Cronoprogrammi/CronoprogrammaPage'))

// Alias per pagine che usano lo stesso componente
const NuovoUtente = UtentePage
const ModificaUtente = UtentePage
const NuovoCliente = ClientePage
const ModificaCliente = ClientePage
const NuovoServizio = ServizioPage
const ModificaServizio = ServizioPage
const NuovoFornitore = FornitorePage
const ModificaFornitori = FornitorePage
const NuovoInvestitore = InvestitorePage
const ModificaInvestitore = InvestitorePage
const NuovoInvestimento = InvestimentoPage
const ModificaInvestimento = InvestimentoPage
const NuovoArticolo = ArticoloPage
const ModificaArticolo = ArticoloPage
const NuovoRicavo = RicavoPage
const ModificaRicavo = RicavoPage
const NuovoCosto = CostoPage
const ModificaCosto = CostoPage
const NuovoRimborso = RimborsoPage
const ModificaRimborso = RimborsoPage
const NuovoProgetto = ProgettoPage
const ModificaProgetto = ProgettoPage
const NuovaCostruzione = CostruzionePage
const ModificaCostruzione = CostruzionePage
const NuovaInstallazione = InstallazionePage
const ModificaInstallazione = InstallazionePage
const NuovoResoconto = ResocontoPage
const ModificaResoconto = ResocontoPage

// BoardPageWrapper — injects all hooks directly (WithNavigate does NOT forward extra props)
function BoardPageWrapper () {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { language } = useContext(LanguageContext)
  return <BoardPage navigate={navigate} location={location} params={params} language={language} />
}

function TicketDetailWrapper () {
  const navigate = useNavigate()
  const params = useParams()
  return <TicketDetailPage navigate={navigate} params={params} />
}

// Loading component
const PageLoader = () => <Loader loading />

export default class App extends Component {
  constructor (props) {
    super(props)
    this.state = {}
  }

  async componentDidMount () {
    // Timeout di inattività temporaneamente disabilitato
    // ActivityTimeout.init()
  }

  componentWillUnmount () {
    // Timeout di inattività temporaneamente disabilitato
    // ActivityTimeout.cleanup()
  }

  render () {
    return (
      <LanguageContextProvider>
      <ThemeContextProvider>
        <ThemeContext.Consumer>
          {({ theme }) => (
            <ThemeProvider theme={theme}>
              <SwallerProvider>
                <GlobalStyles />
                <BrowserRouter>
                  <Container>
                    <WithNavigate Component={NavbarObj} />
                    <LanguageContext.Consumer>
                      {({ language }) => (
                    <Suspense key={language} fallback={<PageLoader />}>
                      <Routes>
                        <Route path='/' exact element={<WithNavigate Component={Landing} />} />
                        <Route path='/login' exact element={<WithNavigate Component={Login} />} />
                        <Route path='/firstlogin' exact element={<WithNavigate Component={FirstLogin} />} />
                        <Route path='/firstotp' exact element={<WithNavigate Component={FirstOtp} />} />
                        <Route path='/resetpassword' exact element={<WithNavigate Component={ResetPassword} />} />
                        <Route path='/home' exact element={<ProtectedRoute><WithNavigate Component={Home} /></ProtectedRoute>} />
                        <Route path='/aziende' exact element={<ProtectedRoute><WithNavigate Component={Aziende} /></ProtectedRoute>} />
                        <Route path='/preventivi' exact element={<ProtectedRoute><WithNavigate Component={Preventivi} /></ProtectedRoute>} />
                        <Route path='/preventivi/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoPreventivo} /></ProtectedRoute>} />
                        <Route path='/fatture' exact element={<ProtectedRoute><WithNavigate Component={Fatture} /></ProtectedRoute>} />
                        <Route path='/fatture/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovaFattura} /></ProtectedRoute>} />
                        <Route path='/fatture/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaFattura} /></ProtectedRoute>} />
                        <Route path='/settaggi' exact element={<ProtectedRoute><WithNavigate Component={Settaggi} /></ProtectedRoute>} />
                        <Route path='/utenti' exact element={<ProtectedRoute><WithNavigate Component={Utenti} /></ProtectedRoute>} />
                        <Route path='/utenti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoUtente} /></ProtectedRoute>} />
                        <Route path='/aziende/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovaAzienda} /></ProtectedRoute>} />
                        <Route path='/clienti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoCliente} /></ProtectedRoute>} />
                        <Route path='/clienti' exact element={<ProtectedRoute><WithNavigate Component={Clienti} /></ProtectedRoute>} />
                        <Route path='/magazzino' exact element={<ProtectedRoute><WithNavigate Component={MagazzinoUnificato} /></ProtectedRoute>} />
                        <Route path='/ordini' exact element={<ProtectedRoute><WithNavigate Component={Ordini} /></ProtectedRoute>} />
                        <Route path='/carrello' exact element={<ProtectedRoute><WithNavigate Component={Carrello} /></ProtectedRoute>} />
                        <Route path='/utenti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaUtente} /></ProtectedRoute>} />
                        <Route path='/aziende/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaAzienda} /></ProtectedRoute>} />
                        <Route path='/clienti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaCliente} /></ProtectedRoute>} />
                        <Route path='/preventivi/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaPreventivo} /></ProtectedRoute>} />
                        <Route path='/variabili' exact element={<ProtectedRoute><WithNavigate Component={Variabili} /></ProtectedRoute>} />
                        <Route path='/servizi' exact element={<ProtectedRoute><WithNavigate Component={Servizi} /></ProtectedRoute>} />
                        <Route path='/servizi/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoServizio} /></ProtectedRoute>} />
                        <Route path='/servizi/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaServizio} /></ProtectedRoute>} />
                        <Route path='/fornitori' exact element={<ProtectedRoute><WithNavigate Component={Fornitori} /></ProtectedRoute>} />
                        <Route path='/fornitori/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoFornitore} /></ProtectedRoute>} />
                        <Route path='/fornitori/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaFornitori} /></ProtectedRoute>} />
                        <Route path='/investitori' exact element={<ProtectedRoute><WithNavigate Component={Investitori} /></ProtectedRoute>} />
                        <Route path='/investitori/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoInvestitore} /></ProtectedRoute>} />
                        <Route path='/investitori/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaInvestitore} /></ProtectedRoute>} />
                        <Route path='/investimenti' exact element={<ProtectedRoute><WithNavigate Component={Investimenti} /></ProtectedRoute>} />
                        <Route path='/investimenti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoInvestimento} /></ProtectedRoute>} />
                        <Route path='/investimenti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaInvestimento} /></ProtectedRoute>} />
                        <Route path='/sconti' exact element={<ProtectedRoute><WithNavigate Component={Sconti} /></ProtectedRoute>} />
                        <Route path='/sconti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoSconto} /></ProtectedRoute>} />
                        <Route path='/sconti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaSconto} /></ProtectedRoute>} />
                        <Route path='/articoli' exact element={<ProtectedRoute><WithNavigate Component={Articoli} /></ProtectedRoute>} />
                        <Route path='/articoli/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoArticolo} /></ProtectedRoute>} />
                        <Route path='/articoli/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaArticolo} /></ProtectedRoute>} />
                        <Route path='/ricavi' exact element={<ProtectedRoute><WithNavigate Component={Ricavi} /></ProtectedRoute>} />
                        <Route path='/ricavi/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoRicavo} /></ProtectedRoute>} />
                        <Route path='/ricavi/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaRicavo} /></ProtectedRoute>} />
                        <Route path='/costi' exact element={<ProtectedRoute><WithNavigate Component={Costi} /></ProtectedRoute>} />
                        <Route path='/costi/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoCosto} /></ProtectedRoute>} />
                        <Route path='/costi/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaCosto} /></ProtectedRoute>} />
                        <Route path='/rimborsi' exact element={<ProtectedRoute><WithNavigate Component={Rimborsi} /></ProtectedRoute>} />
                        <Route path='/rimborsi/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoRimborso} /></ProtectedRoute>} />
                        <Route path='/rimborsi/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaRimborso} /></ProtectedRoute>} />
                        <Route path='/scatole' exact element={<ProtectedRoute><WithNavigate Component={Scatole} /></ProtectedRoute>} />
                        <Route path='/scatole/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaScatola} /></ProtectedRoute>} />
                        <Route path='/progetti' exact element={<ProtectedRoute><WithNavigate Component={Progetti} /></ProtectedRoute>} />
                        <Route path='/progetti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoProgetto} /></ProtectedRoute>} />
                        <Route path='/progetti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaProgetto} /></ProtectedRoute>} />
                        <Route path='/costruzioni' exact element={<ProtectedRoute><WithNavigate Component={Costruzioni} /></ProtectedRoute>} />
                        <Route path='/costruzioni/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovaCostruzione} /></ProtectedRoute>} />
                        <Route path='/costruzioni/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaCostruzione} /></ProtectedRoute>} />
                        <Route path='/costruzioni/fotovoltaico' exact element={<ProtectedRoute><WithNavigate Component={FotovoltaicoPage} /></ProtectedRoute>} />
                        <Route path='/installazioni' exact element={<ProtectedRoute><WithNavigate Component={Installazioni} /></ProtectedRoute>} />
                        <Route path='/installazioni/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovaInstallazione} /></ProtectedRoute>} />
                        <Route path='/installazioni/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaInstallazione} /></ProtectedRoute>} />
                        <Route path='/canoni' exact element={<ProtectedRoute><WithNavigate Component={Canoni} /></ProtectedRoute>} />
                        <Route path='/canoni/nuovo' exact element={<ProtectedRoute><WithNavigate Component={AggiungiCanoni} /></ProtectedRoute>} />
                        <Route path='/canoni/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaCanoni} /></ProtectedRoute>} />
                        <Route path='/analisi' exact element={<ProtectedRoute><WithNavigate Component={Analisi} /></ProtectedRoute>} />
                        <Route path='/ecommerce' exact element={<ProtectedRoute><WithNavigate Component={ProdottiEcommerce} /></ProtectedRoute>} />
                        <Route path='/ecommerce/prodotti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={ProdottoEcommercePage} /></ProtectedRoute>} />
                        <Route path='/ecommerce/prodotti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ProdottoEcommercePage} /></ProtectedRoute>} />
                        <Route path='/ecommerce/categorie' exact element={<ProtectedRoute><WithNavigate Component={CategorieEcommerce} /></ProtectedRoute>} />
                        <Route path='/ecommerce/clienti' exact element={<ProtectedRoute><WithNavigate Component={ClientiEcommerce} /></ProtectedRoute>} />
                        <Route path='/board' exact element={<ProtectedRoute><WithNavigate Component={BoardList} /></ProtectedRoute>} />
                        <Route path='/board/:id' exact element={<ProtectedRoute><BoardPageWrapper /></ProtectedRoute>} />
                        <Route path='/ticket' exact element={<ProtectedRoute><WithNavigate Component={TicketPage} /></ProtectedRoute>} />
                        <Route path='/ticket/:id' exact element={<ProtectedRoute><TicketDetailWrapper /></ProtectedRoute>} />
                        <Route path='/resoconti' exact element={<ProtectedRoute><WithNavigate Component={Resoconti} /></ProtectedRoute>} />
                        <Route path='/resoconti/nuovo' exact element={<ProtectedRoute><WithNavigate Component={NuovoResoconto} /></ProtectedRoute>} />
                        <Route path='/resoconti/modifica' exact element={<ProtectedRoute><WithNavigate Component={ModificaResoconto} /></ProtectedRoute>} />
                        <Route path='/cronoprogrammi' exact element={<ProtectedRoute><WithNavigate Component={Cronoprogrammi} /></ProtectedRoute>} />
                        <Route path='/cronoprogrammi/:id' exact element={<ProtectedRoute><WithNavigate Component={CronoprogrammaPage} /></ProtectedRoute>} />
                      </Routes>
                    </Suspense>
                      )}
                    </LanguageContext.Consumer>
                  </Container>
                </BrowserRouter>
              </SwallerProvider>
            </ThemeProvider>
          )}
        </ThemeContext.Consumer>
      </ThemeContextProvider>
      </LanguageContextProvider>
    )
  }
}
